import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Plus, Copy, CheckCircle2, Link2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { RoleBadge } from "@/components/RoleBadge";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const ASSIGNABLE_ROLES = ["admin", "researcher", "viewer", "client_viewer", "owner"];

function buildInviteLink(token) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}/invite/${token}`;
}

/** Try legacy execCommand('copy') via a hidden textarea. Returns true on success. */
function legacyCopy(text) {
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "0";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (_) {
    return false;
  }
}

async function tryCopy(text) {
  // Modern clipboard API (requires secure context + permission).
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (_) {
      // fall through to legacy
    }
  }
  return legacyCopy(text);
}

export default function UsersSettings() {
  const { t } = useTranslation(["settings", "common"]);
  const { user: currentUser } = useAuth();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Invite modal state
  const [open, setOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("researcher");
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null); // { token, email, role }
  const [inviteError, setInviteError] = useState("");
  const [copied, setCopied] = useState(false);
  const [copyHint, setCopyHint] = useState(false);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (err) {
      toast.error(formatApiError(err, t("common:errors.unexpected")));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const resetInviteModal = () => {
    setInviteEmail("");
    setInviteRole("researcher");
    setInviteResult(null);
    setInviteError("");
    setCopied(false);
    setCopyHint(false);
  };

  const submitInvite = async (e) => {
    e.preventDefault();
    setInviteSubmitting(true);
    setInviteError("");
    try {
      const { data } = await api.post("/auth/invites", {
        email: inviteEmail.trim(),
        role: inviteRole,
      });
      setInviteResult({ token: data.token, email: data.email, role: data.role });
    } catch (err) {
      if (err?.response?.status === 409) {
        setInviteError(t("settings:invite.emailTaken"));
      } else {
        setInviteError(formatApiError(err, t("common:errors.unexpected")));
      }
    } finally {
      setInviteSubmitting(false);
    }
  };

  const copyLink = async (link) => {
    if (await tryCopy(link)) {
      setCopied(true);
      toast.success(t("common:copied"));
      setTimeout(() => setCopied(false), 2200);
      return;
    }
    // Both modern + legacy copy failed (e.g. iframe sandbox without
    // clipboard permission). Surface a graceful manual-copy hint instead
    // of an "unexpected error" toast.
    setCopyHint(true);
    setTimeout(() => setCopyHint(false), 6000);
  };

  const changeRole = async (u, newRole) => {
    if (newRole === u.role) return;
    try {
      const { data } = await api.patch(`/users/${u.id}`, { role: newRole });
      setUsers((list) => list.map((x) => (x.id === u.id ? data : x)));
      toast.success(t("common:saved"));
    } catch (err) {
      toast.error(formatApiError(err, t("common:errors.unexpected")));
    }
  };

  const inviteLink = inviteResult ? buildInviteLink(inviteResult.token) : "";

  return (
    <div className="space-y-8" data-testid="users-settings-page">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-2">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            Settings
          </span>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-slate-900">
            {t("settings:users.title")}
          </h1>
          <p className="max-w-xl text-sm text-slate-500">{t("settings:users.subtitle")}</p>
        </div>
        <Button
          onClick={() => {
            resetInviteModal();
            setOpen(true);
          }}
          data-testid="open-invite-modal"
          className="h-10 bg-slate-900 text-white hover:bg-slate-800"
        >
          <Plus className="mr-1 h-4 w-4" strokeWidth={2} /> {t("settings:users.inviteUser")}
        </Button>
      </header>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="users-table-container">
        {loading ? (
          <div className="flex items-center justify-center px-6 py-10 text-sm text-slate-500">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("common:loading")}
          </div>
        ) : users.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-slate-500">
            {t("settings:users.noUsers")}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-b border-slate-200 bg-slate-50">
                <TableHead className="px-6 py-3 text-[11px] uppercase tracking-wider text-slate-500">
                  {t("settings:users.columns.name")}
                </TableHead>
                <TableHead className="px-6 py-3 text-[11px] uppercase tracking-wider text-slate-500">
                  {t("settings:users.columns.email")}
                </TableHead>
                <TableHead className="px-6 py-3 text-[11px] uppercase tracking-wider text-slate-500">
                  {t("settings:users.columns.role")}
                </TableHead>
                <TableHead className="px-6 py-3 text-[11px] uppercase tracking-wider text-slate-500">
                  {t("settings:users.columns.status")}
                </TableHead>
                <TableHead className="px-6 py-3 text-right text-[11px] uppercase tracking-wider text-slate-500">
                  {t("settings:users.columns.actions")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => {
                const isSelf = u.id === currentUser?.id;
                return (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`} className="border-slate-100">
                    <TableCell className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-slate-900">{u.name}</span>
                        {isSelf && (
                          <span className="text-[11px] text-slate-400">{t("common:you")}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="px-6 py-4 text-sm text-slate-600">{u.email}</TableCell>
                    <TableCell className="px-6 py-4">
                      <RoleBadge role={u.role} />
                    </TableCell>
                    <TableCell className="px-6 py-4 text-sm text-slate-600">
                      {t(`common:status.${u.status}`)}
                    </TableCell>
                    <TableCell className="px-6 py-4 text-right">
                      <div className="flex justify-end">
                        <Select
                          value={u.role}
                          onValueChange={(role) => changeRole(u, role)}
                          disabled={isSelf}
                        >
                          <SelectTrigger
                            data-testid={`user-role-select-${u.id}`}
                            className="ml-auto h-9 w-44"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent align="end">
                            {ASSIGNABLE_ROLES.map((r) => (
                              <SelectItem
                                key={r}
                                value={r}
                                data-testid={`user-role-option-${u.id}-${r}`}
                              >
                                {t(`common:roles.${r}`)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) resetInviteModal();
        }}
      >
        <DialogContent data-testid="invite-modal" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-semibold tracking-tight">
              {t("settings:invite.title")}
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              {t("settings:invite.subtitle")}
            </DialogDescription>
          </DialogHeader>

          {!inviteResult ? (
            <form onSubmit={submitInvite} className="space-y-5" noValidate>
              <div className="space-y-2">
                <Label htmlFor="invite-email" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {t("settings:invite.emailLabel")}
                </Label>
                <Input
                  id="invite-email"
                  type="email"
                  required
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder={t("settings:invite.emailPlaceholder")}
                  data-testid="invite-email-input"
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {t("settings:invite.roleLabel")}
                </Label>
                <Select value={inviteRole} onValueChange={setInviteRole}>
                  <SelectTrigger data-testid="invite-role-select" className="h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ASSIGNABLE_ROLES.map((r) => (
                      <SelectItem key={r} value={r} data-testid={`invite-role-option-${r}`}>
                        {t(`common:roles.${r}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {inviteError && (
                <p
                  data-testid="invite-modal-error"
                  role="alert"
                  className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                >
                  {inviteError}
                </p>
              )}

              <DialogFooter>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setOpen(false)}
                  data-testid="invite-cancel-button"
                >
                  {t("common:cancel")}
                </Button>
                <Button
                  type="submit"
                  disabled={inviteSubmitting}
                  data-testid="invite-submit-button"
                  className="bg-slate-900 text-white hover:bg-slate-800"
                >
                  {inviteSubmitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    t("settings:invite.submit")
                  )}
                </Button>
              </DialogFooter>
            </form>
          ) : (
            <div className="space-y-5" data-testid="invite-result">
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                <p className="flex items-center gap-2 text-sm font-medium text-emerald-800">
                  <CheckCircle2 className="h-4 w-4" strokeWidth={2} />
                  {t("settings:invite.linkReady")}
                </p>
                <p className="mt-1 text-xs text-emerald-700/80">
                  {t("settings:invite.linkHelp", { email: inviteResult.email })}
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <Link2 className="mr-1 inline h-3 w-3" strokeWidth={2} /> Invite link
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    readOnly
                    value={inviteLink}
                    title={inviteLink}
                    data-testid="invite-link-input"
                    className="h-11 min-w-0 flex-1 font-mono text-xs"
                  />
                  <Button
                    type="button"
                    onClick={() => copyLink(inviteLink)}
                    data-testid="invite-link-copy"
                    className={
                      copied
                        ? "h-11 flex-shrink-0 bg-emerald-600 text-white hover:bg-emerald-700"
                        : "h-11 flex-shrink-0 bg-slate-900 text-white hover:bg-slate-800"
                    }
                  >
                    {copied ? (
                      <CheckCircle2 className="h-4 w-4" strokeWidth={2} />
                    ) : (
                      <Copy className="h-4 w-4" strokeWidth={2} />
                    )}
                  </Button>
                </div>
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    loadUsers();
                  }}
                  data-testid="invite-close-button"
                  className="bg-slate-900 text-white hover:bg-slate-800"
                >
                  {t("settings:invite.close")}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
